import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCallStatusLabel,
  getVerificationStatusLabel,
  getPropertyTenantStatusLabel,
} from './screeningEnums';

// Build status steps from API fields: screening_call_status (0:pending,1:calling,2:completed,3:failed),
// verification_status / reference_check_status (0:pending,1:verified,2:rejected), property_tenant_status (0:pending,1:verified,2:rejected)
const buildStatusSteps = (data) => {
  if (!data) return [];
  return [
    { name: 'Screening', status: getCallStatusLabel(data.screening_call_status) },
    { name: 'Documents for verification', status: getVerificationStatusLabel(data.verification_status ?? data.document_verification_status) },
    { name: 'Reference Check', status: getVerificationStatusLabel(data.reference_check_status) },
    { name: 'Landlord Review', status: getVerificationStatusLabel(data.landlord_review_status) },
    { name: 'Final Decision', status: getPropertyTenantStatusLabel(data.property_tenant_status ?? data.final_screening_status) },
  ];
};

const ScreeningDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [screening, setScreening] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [backgroundCheckLink, setBackgroundCheckLink] = useState('tenant-screen.ai/prop/123-oak');

  // Sample screening data - in a real app, fetch by id and use buildStatusSteps(apiData)
  const screeningData = {
    id: 1,
    image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800',
    address: '123 Oak Street, Apt 4B',
    city: 'San Francisco, CA',
    zipCode: '94102',
    code: '404023',
    landlordName: 'Mark Liu',
    propertyType: 'Apartment',
    price: '$345',
    sqft: '12,00ft',
    type: 'Apartment',
    deposit: '$200',
    bedrooms: 2,
    bathrooms: 2,
    parking: true,
    furnished: 'Semi',
    garden: false,
    wifi: true,
    ac: true,
    description: "Welcome to this stunning modern apartment in the heart of downtown San Francisco. This beautifully renovated 2-bedroom, 2-bathroom unit features an open floor plan with abundant natural light, hardwood floors throughout, and a chef's kitchen.",
    // Enum-based: 0=pending, 1=calling, 2=completed, 3=failed for voice; 0/1/2 for doc/ref/final
    screening_call_status: '2',
    verification_status: '0',
    reference_check_status: '0',
    landlord_review_status: '0',
    property_tenant_status: '0',
  };

  useEffect(() => {
    // When using API: fetch screening by id, then set screening with statusSteps: buildStatusSteps(apiData)
    const payload = { ...screeningData, statusSteps: buildStatusSteps(screeningData) };
    setScreening(payload);
  }, [id]);

  const handleCopyLink = () => {
    if (!backgroundCheckLink) return;
    if (navigator?.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(backgroundCheckLink);
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = backgroundCheckLink;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleOpenBackgroundLink = () => {
    if (!backgroundCheckLink) return;
    window.open(backgroundCheckLink, '_blank', 'noopener,noreferrer');
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'verified' || s === 'done') return '#155724';
    if (s === 'rejected' || s === 'failed') return '#721c24';
    if (s === 'calling') return '#004085';
    return '#856404'; // Pending
  };

  const getStatusBgColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'verified' || s === 'done') return '#d4edda';
    if (s === 'rejected' || s === 'failed') return '#f8d7da';
    if (s === 'calling') return '#cce5ff';
    return 'rgba(191, 140, 67, 0.2)'; // Pending
  };

  const getLineColor = (status) => {
    if (status === 'Done') return '#3C83F6';
    return '#93c5fd';
  };

  const getProgressLineGradient = () => {
    const steps = screening.statusSteps;
    if (!steps.length) return 'transparent';
    const n = steps.length;
    const stops = steps.map((step, i) => {
      const c = getLineColor(step.status);
      const start = (i / n) * 100;
      const end = ((i + 1) / n) * 100;
      return `${c} ${start}% ${end}%`;
    });
    return `linear-gradient(to bottom, ${stops.join(', ')})`;
  };

  if (!screening) {
    return <div>Loading...</div>;
  }

  return (
    <div className="property-detail-page">
      <div className="property-detail-container">
        <div className="property-detail-left">
          <div className="property-detail-header">
            <div className="property-detail-address-section">
              <h1 className="property-detail-address">{screening.address}</h1>
              <div className="property-detail-code-location">
                <span className="property-detail-code">Code: {screening.code}</span>
                <span className="property-detail-location">{screening.city} {screening.zipCode}</span>
              </div>
            </div>
          </div>

          <div className="property-detail-image-container">
            {imageError ? (
              <div className="property-detail-image-placeholder"></div>
            ) : (
              <img
                src={screening.image}
                alt={screening.address}
                className="property-detail-image"
                onError={() => setImageError(true)}
              />
            )}
          </div>

          <div className="property-detail-about">
            <h2 className="property-detail-about-title">About this Property</h2>
            <p className="property-detail-about-description">{screening.description}</p>
          </div>

          <div className="property-detail-financials">
            <div className="property-detail-financial-card property-detail-financial-card-rent">
              <div className="property-detail-financial-label">Monthly Rent</div>
              <div className="property-detail-financial-value">{screening.price}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Sq. ft</div>
              <div className="property-detail-financial-value">{screening.sqft}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Type</div>
              <div className="property-detail-financial-value">{screening.type}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Deposit</div>
              <div className="property-detail-financial-value">{screening.deposit}</div>
            </div>
          </div>

          <div className="screening-features">
            <h3 className="screening-features-title">Property Features</h3>
            <div className="screening-features-grid">
              <div className="screening-features-col">
                <div className="screening-features-item">
                  <span className="screening-features-item-inner">
                    <span className="screening-features-label">Wifi:</span>
                    <span className="screening-features-value">{screening.wifi ? 'Yes' : 'No'}</span>
                  </span>
                </div>
                <div className="screening-features-item">
                  <span className="screening-features-item-inner">
                    <span className="screening-features-label">AC:</span>
                    <span className="screening-features-value">{screening.ac ? 'Yes' : 'No'}</span>
                  </span>
                </div>
                <div className="screening-features-item">
                  <span className="screening-features-item-inner">
                    <span className="screening-features-label">Bedroom:</span>
                    <span className="screening-features-value">{screening.bedrooms}</span>
                  </span>
                </div>
                <div className="screening-features-item">
                  <span className="screening-features-item-inner">
                    <span className="screening-features-label">Bathroom:</span>
                    <span className="screening-features-value">{screening.bathrooms}</span>
                  </span>
                </div>
              </div>
              <div className="screening-features-divider" aria-hidden />
              <div className="screening-features-col">
                <div className="screening-features-item">
                  <span className="screening-features-item-inner">
                    <span className="screening-features-label">Parking:</span>
                    <span className="screening-features-value">{screening.parking ? 'Yes' : 'No'}</span>
                  </span>
                </div>
                <div className="screening-features-item">
                  <span className="screening-features-item-inner">
                    <span className="screening-features-label">Furnished:</span>
                    <span className="screening-features-value">{screening.furnished}</span>
                  </span>
                </div>
                <div className="screening-features-item">
                  <span className="screening-features-item-inner">
                    <span className="screening-features-label">Garden:</span>
                    <span className="screening-features-value">{screening.garden ? 'Yes' : 'No'}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="property-detail-right">
          <div className="screening-status-section">
            <h3 className="screening-status-title">Status</h3>
            <div className="screening-status-steps">
              <div 
                className="screening-status-progress-line" 
                aria-hidden 
                style={{ background: getProgressLineGradient() }}
              />
              {screening.statusSteps.map((step, index) => (
                <div key={index} className="screening-status-step">
                  <div className="screening-status-step-indicator">
                    <div 
                      className="screening-status-step-dot" 
                      style={{ backgroundColor: getLineColor(step.status) }}
                    />
                  </div>
                  <div className="screening-status-step-content">
                    <span className="screening-status-step-name">{step.name}</span>
                    <span 
                      className="screening-status-step-badge"
                      style={{ 
                        color: getStatusColor(step.status),
                        backgroundColor: getStatusBgColor(step.status)
                      }}
                    >
                      {step.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="screening-invitation-section">
            <h3 className="screening-invitation-title">Begin Application Screening</h3>
            <p className="screening-invitation-text">
              To complete your application, please proceed with the automated voice screening process.
            </p>
            <p className="screening-invitation-text">
              Select the link below to open it on your mobile device and begin the screening.
            </p>
            <div className="screening-invitation-property">
              <p className="screening-invitation-property-line">
                <span className="screening-invitation-label">Property Address:</span>{' '}
                <span className="screening-invitation-value">
                  {screening.address}, {screening.city}, {screening.zipCode}
                </span>
              </p>
              <p className="screening-invitation-property-line">
                <span className="screening-invitation-label">Property Code:</span>{' '}
                <span className="screening-invitation-value">{screening.code}</span>
              </p>
            </div>
            <div className="screening-invitation-input-wrapper">
              <button
                type="button"
                className="screening-invitation-input"
                onClick={handleOpenBackgroundLink}
                disabled={!backgroundCheckLink}
              >
                Begin Screening
              </button>
              <button 
                className="screening-invitation-copy"
                onClick={handleCopyLink}
                disabled={!backgroundCheckLink}
              >
                <img 
                  src="/assets/images/CopyIcon.svg" 
                  alt="Copy" 
                  className="screening-invitation-copy-icon"
                />
              </button>
            </div>
            <p className="screening-invitation-note">
              <strong>Important:</strong> Please ensure that you answer all questions clearly and accurately. Your
              responses will be submitted to the landlord for review as part of your application.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreeningDetail;
